import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useComplianceService } from "../apis/compliance/useComplianceService";
import {
  TCompliance,
  TCreateComplianceRequest,
  TUpdateComplianceRequest,
  TComplianceTemplate,
  TApplyTemplateRequest,
} from "../apis/compliance/compliance.interfaces";
import { useInstituteContext } from "../context/institute.context";

export const useComplianceHook = () => {
  const { getCompliances } = useComplianceService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TCompliance[]>({
    queryKey: ["compliances", selectedInstitute?.id],
    queryFn: () => getCompliances(selectedInstitute?.id),
    enabled: !!selectedInstitute?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateComplianceHook = () => {
  const { createCompliance } = useComplianceService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<{ id: string }, Error, TCreateComplianceRequest>({
    mutationFn: (request: TCreateComplianceRequest) =>
      createCompliance(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["compliances", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdateComplianceHook = () => {
  const { updateCompliance } = useComplianceService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    void,
    Error,
    { id: string; request: TUpdateComplianceRequest }
  >({
    mutationFn: ({ id, request }) => updateCompliance(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["compliances", selectedInstitute?.id],
      });
    },
  });
};

export const useDeleteComplianceHook = () => {
  const { deleteCompliance } = useComplianceService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteCompliance(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["compliances", selectedInstitute?.id],
      });
    },
  });
};

export const useComplianceTemplatesHook = () => {
  const { getTemplates } = useComplianceService();

  return useQuery<TComplianceTemplate[]>({
    queryKey: ["compliance-templates"],
    queryFn: () => getTemplates(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useApplyTemplateHook = () => {
  const { applyTemplate } = useComplianceService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TCompliance[], Error, TApplyTemplateRequest>({
    mutationFn: (request: TApplyTemplateRequest) => applyTemplate(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["compliances", selectedInstitute?.id],
      });
    },
  });
};
